"use client"

import { Badge, Skeleton, formatBriefDate, cn } from '@yeshuman/sdk'
import { Briefcase, ClipboardCheck } from 'lucide-react'
import { useProfileInterviewOutcomes } from '../../hooks/use-briefs'
import { interviewOutcomeBadgeVariant } from '../../lib/interview-outcome-display'

export type ProfileInterviewOutcomesSectionProps = {
  profileId: string | null | undefined
  className?: string
}

/**
 * Outcomes recorded for this person on jobs linked to the signed-in recruiter's briefs.
 * Rendered as pack content under {@link PersonProfileReadView} on the recruiter person page and preview.
 */
export function ProfileInterviewOutcomesSection({ profileId, className }: ProfileInterviewOutcomesSectionProps) {
  const { outcomes, isLoading, error } = useProfileInterviewOutcomes(profileId)

  return (
    <section
      className={cn('w-full min-w-0 space-y-2', className)}
      aria-labelledby="profile-interview-outcomes-heading"
      data-testid="profile-interview-outcomes"
    >
      <h2
        id="profile-interview-outcomes-heading"
        className="flex items-center gap-2 text-sm font-semibold"
      >
        <ClipboardCheck className="size-4 shrink-0" aria-hidden />
        Interview outcomes
      </h2>
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Could not load interview outcomes'}
        </p>
      ) : outcomes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No outcomes recorded yet. Set one from a shortlist card on a brief with a linked job.
        </p>
      ) : (
        <ul className="space-y-2">
          {outcomes.map((outcome) => {
            const jobLine = outcome.opportunity_title || 'Linked job'
            const briefLine = outcome.brief_title && outcome.brief_title !== jobLine ? outcome.brief_title : null
            const notes = outcome.notes?.trim()
            return (
              <li
                key={outcome.id}
                className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2"
                data-testid="profile-interview-outcome-row"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 text-sm font-medium">
                      <Briefcase className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="truncate">{jobLine}</span>
                    </p>
                    {briefLine ? (
                      <p className="mt-0.5 text-xs text-muted-foreground">Brief: {briefLine}</p>
                    ) : null}
                  </div>
                  <Badge variant={interviewOutcomeBadgeVariant(outcome.state)} className="shrink-0 text-xs font-medium">
                    {outcome.state_label}
                  </Badge>
                </div>
                {notes ? <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{notes}</p> : null}
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatBriefDate(outcome.updated_at)}
                  {outcome.updated_by_name ? ` · ${outcome.updated_by_name}` : ''}
                </p>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
